// Thành phần hiển thị cấp trang theo DESIGN.md: tiêu đề trang, thanh công cụ, chip, KPI, panel, ô dữ liệu.
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { shiftMonth } from '../lib/domain';
import { cx } from '../lib/cx';
import { fmtCountdown, initials, monthKey, monthLabel } from '../lib/format';
import { serverNow, useServerNow } from '../lib/server-clock';
import { Btn } from './button';
import { Icon } from './icon';
import { RulesButton } from './overlay';

export function PageHeader({ title, meta, rules, actions, children }: { title: ReactNode; meta?: ReactNode; rules?: ReactNode; actions?: ReactNode; children?: ReactNode }) {
  return (
    <header className="ph">
      <div className="ph-row">
        <div className="ph-title">
          <h1>{title}</h1>
          {/* Dòng meta luôn có (khoảng trắng khi chưa có số liệu) ⇒ tiêu đề không đổi chiều cao khi dữ liệu về */}
          <div className="ph-meta" aria-hidden={meta ? undefined : true}>
            {meta || '\u00a0'}
          </div>
        </div>
        <div className="ph-actions">
          {rules && <RulesButton>{rules}</RulesButton>}
          {actions}
        </div>
      </div>
      {children && <div className="ph-tabs">{children}</div>}
    </header>
  );
}

export function Toolbar({ children, right }: { children?: ReactNode; right?: ReactNode }) {
  return (
    <div className="toolbar">
      <div className="toolbar-l">{children}</div>
      {right && <div className="toolbar-r">{right}</div>}
    </div>
  );
}

export function Tabs<K extends string>({ value, onChange, tabs }: { value: K; onChange: (k: K) => void; tabs: [K, ReactNode, (number | null)?][] }) {
  // Màn hẹp (thanh tab cuộn ngang): đưa tab đang chọn vào khung nhìn (VD ?tab=all mở thẳng tab cuối) — chỉ cuộn ngang thanh tab.
  // Chạy lại khi số đếm đổi (tab rộng ra) và khi phông web nạp xong
  const ref = useRef<HTMLDivElement>(null);
  const sig = tabs.map(([k, , c]) => `${k}:${c ?? ''}`).join('|');
  useEffect(() => {
    const ensure = () => {
      const box = ref.current;
      const on = box?.querySelector<HTMLElement>('[aria-selected="true"]');
      if (!box || !on || box.scrollWidth <= box.clientWidth) return;
      const b = box.getBoundingClientRect();
      const r = on.getBoundingClientRect();
      if (r.left < b.left) box.scrollLeft -= b.left - r.left + 16;
      else if (r.right > b.right) box.scrollLeft += r.right - b.right + 16;
    };
    ensure();
    let live = true;
    void document.fonts?.ready.then(() => live && ensure());
    return () => {
      live = false;
    };
  }, [value, sig]);
  return (
    <div className="tabs" role="tablist" ref={ref}>
      {tabs.map(([k, label, count]) => (
        <button key={k} type="button" role="tab" aria-selected={value === k} className={cx('tab', value === k && 'on')} onClick={() => onChange(k)}>
          {label}
          {count != null && <span className="c">{count}</span>}
        </button>
      ))}
    </div>
  );
}

export function FilterChips<K extends string>({ value, onChange, items }: { value: K; onChange: (k: K) => void; items: [K, ReactNode, (number | null)?][] }) {
  return (
    <div className="fchips" role="tablist">
      {items.map(([k, label, n]) => (
        <button key={k} type="button" role="tab" aria-selected={value === k} className={cx('fchip', value === k && 'on')} onClick={() => onChange(k)}>
          {label}
          {n != null && <span className="n">{n}</span>}
        </button>
      ))}
    </div>
  );
}

export function Search({ value, onChange, placeholder = 'Tìm kiếm…', id }: { value: string; onChange: (v: string) => void; placeholder?: string; id?: string }) {
  return (
    <div className="search">
      <Icon n="search" s={15} />
      <input id={id} className="input" value={value} placeholder={placeholder} aria-label={placeholder} onChange={(e) => onChange(e.currentTarget.value)} />
    </div>
  );
}

export type Tone = '' | 'good' | 'warn' | 'bad' | 'info' | 'violet' | 'brand' | 'neutral';
export function StatusChip({ tone = '', children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return (
    <span className={cx('chip-s', tone !== 'neutral' && tone)} title={title}>
      <i />
      {children}
    </span>
  );
}
export function Badge({ tone = '', dot, children, title }: { tone?: Tone | string; dot?: boolean; children: ReactNode; title?: string }) {
  return (
    <span className={cx('badge', tone, dot && 'dot')} title={title}>
      {children}
    </span>
  );
}

/** Sắc riêng của từng người (CSS .avatar pha nền nhạt + chữ đậm cùng sắc; mỗi mã ≥ 4.5:1 trên nền trắng). */
const AV_COLORS = ['#2f6fd6', '#b8442c', '#11785a', '#8f5f12', '#c2477e', '#237334', '#6750c9', '#c43f3f', '#0f7b8c', '#7a5bb5'];
export function avatarColor(seed: string) {
  let h = 0;
  for (const ch of String(seed || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}
export function Avatar({ id, name, size = '', title }: { id: string; name: string; size?: '' | 'sm' | 'lg'; title?: string }) {
  return (
    <span className={cx('avatar', size)} style={{ '--av': avatarColor(id) } as CSSProperties} title={title ?? name}>
      {initials(name)}
    </span>
  );
}

/** Avatar + tên (+ dòng phụ) — thay StaffName của prototype; DTO đã mang tên hiển thị (ownerName, staffName…). */
export function Person({ id, name, sub, size = 'sm' }: { id: string | null | undefined; name: string | null | undefined; sub?: ReactNode; size?: '' | 'sm' | 'lg' }) {
  if (!name) return <span className="muted">—</span>;
  return (
    <span className="row" style={{ gap: 8, minWidth: 0 }}>
      <Avatar id={id ?? name} name={name} size={size} />
      <span className="grow" style={{ minWidth: 0 }}>
        <span className="ellipsis" style={{ display: 'block' }}>
          {name}
        </span>
        {sub && <span className="tiny muted">{sub}</span>}
      </span>
    </span>
  );
}

/** Chuyển tháng 'YYYY-MM' (nút trái/phải); khoá tháng sau `max` (mặc định tháng hiện tại theo giờ máy chủ). */
export function MonthSwitcher({ value, onChange, max, label, noun = 'Tháng' }: { value: string; onChange: (mk: string) => void; max?: string; label?: (mk: string) => ReactNode; noun?: string }) {
  const limit = max ?? monthKey(serverNow());
  return (
    <div className="row">
      <Btn icon="left" onClick={() => onChange(shiftMonth(value, -1))} aria-label={`${noun} trước`} />
      <span className="strong" style={{ minWidth: 110, textAlign: 'center' }}>
        {label ? label(value) : monthLabel(value)}
      </span>
      <Btn icon="right" onClick={() => onChange(shiftMonth(value, 1))} disabled={value >= limit} aria-label={`${noun} sau`} />
    </div>
  );
}

/**
 * Đếm ngược tới `until` theo giờ máy chủ (mm:ss / h:mm:ss), tự cập nhật mỗi giây; onExpire gọi một lần khi về 0.
 * `until` không đọc được ⇒ "—", không gọi onExpire.
 */
export function Countdown({ until, className = 'mono', onExpire }: { until: string | number | Date | null | undefined; className?: string; onExpire?: () => void }) {
  const now = useServerNow(1000);
  const end = until == null || until === '' ? NaN : typeof until === 'number' ? until : new Date(until).getTime();
  const valid = Number.isFinite(end);
  const left = end - now;
  const fired = useRef<number | null>(null);
  useEffect(() => {
    if (!valid || left > 0 || !onExpire || fired.current === end) return;
    fired.current = end;
    onExpire();
  }, [valid, left, end, onExpire]);
  return <span className={className}>{valid ? fmtCountdown(left) : '—'}</span>;
}

export function KPI({ label, value, foot, delta, deltaGood = true, onClick }: { label: ReactNode; value: ReactNode; foot?: ReactNode; delta?: number | null; deltaGood?: boolean; onClick?: () => void }) {
  const up = delta != null && delta >= 0;
  return (
    <div
      className={cx('kpi', onClick && 'click')}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick &&
        ((e) => {
          // Enter / Space trên chính ô KPI (như nút) ⇒ bấm
          if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          onClick();
        })
      }
    >
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-foot">
        {delta != null && <span className={cx('kpi-delta', up === deltaGood ? 'pos' : 'neg')}>{`${up ? '+' : '−'}${Math.abs(delta).toFixed(0)}%`}</span>}
        {foot}
      </div>
    </div>
  );
}

export function Meter({ pct, tone = '' }: { pct: number; tone?: string }) {
  const v = Math.max(0, Math.min(100, pct));
  return (
    <div className={cx('meter', tone)} role="meter" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100}>
      <i style={{ width: `${v}%` }} />
    </div>
  );
}

export function Section({ title, actions, children, flush }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section className="panel">
      {(title || actions) && (
        <div className="panel-head">
          <h2>{title}</h2>
          {actions && <div className="row wrap">{actions}</div>}
        </div>
      )}
      <div className={flush ? undefined : 'panel-body'}>{children}</div>
    </section>
  );
}
export const SectionTitle = ({ children, right }: { children: ReactNode; right?: ReactNode }) => (
  <div className="sec-title">
    <span>{children}</span>
    {right}
  </div>
);

export function Card({ title, hint, actions, children, flush, className, style }: { title?: ReactNode; hint?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean; className?: string; style?: CSSProperties }) {
  return (
    <section className={cx('card', className)} style={style}>
      {(title || actions) && (
        <div className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {hint && <div className="hint">{hint}</div>}
          </div>
          {actions && <div className="row wrap">{actions}</div>}
        </div>
      )}
      <div className={cx('card-body', flush && 'flush')}>{children}</div>
    </section>
  );
}

/**
 * Trạng thái trống theo DESIGN.md: một dòng chữ + một nút, không minh hoạ. `children` (tuỳ chọn) chỉ là một dòng phụ ngắn
 * (lý do lỗi, việc cần làm) — giải thích nghiệp vụ đặt ở Quy tắc của trang.
 */
export function Empty({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/** Ô đầu dòng: tiêu đề + dòng phụ. */
export const Cell = ({ title, sub, mono }: { title: ReactNode; sub?: ReactNode; mono?: boolean }) => (
  <div className="cell2">
    <div className={cx('t', mono && 'mono')}>{title}</div>
    {sub && <div className="s">{sub}</div>}
  </div>
);

export function Splash({ text = 'Đang tải dữ liệu…', word }: { text?: string; word: string }) {
  return (
    <div className="splash">
      <div className="loading-card">
        <span className="gate-word sm">{word}</span>
        <div className="small dim">{text}</div>
        <div className="loading-bar">
          <i />
        </div>
      </div>
    </div>
  );
}
